// Format number in Indian numbering system (Lakhs, Crores)
// Turn any API error (string detail, FastAPI 422 list, network failure) into a readable message
export const getErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => {
      const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : '';
      const msg = (d.msg || '').replace(/^Value error, /, '');
      return field && field !== 'body' ? `${String(field).replace(/_/g, ' ')}: ${msg}` : msg;
    }).join('. ');
  }
  if (error?.response?.status === 429) return 'Too many attempts. Please wait 15 minutes and try again.';
  if (error?.response?.status >= 500) return 'Our server had a problem. Please try again in a moment.';
  if (error?.request && !error?.response) return 'Cannot reach Laksh servers. Check your internet connection.';
  return fallback;
};

export const formatIndianCurrency = (num, showSymbol = true) => {
  if (num === null || num === undefined) return showSymbol ? '₹0' : '0';
  
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  const symbol = showSymbol ? '₹' : '';
  
  if (absNum >= 10000000) {
    return `${sign}${symbol}${(absNum / 10000000).toFixed(2)} Cr`;
  } else if (absNum >= 100000) {
    return `${sign}${symbol}${(absNum / 100000).toFixed(2)} L`;
  } else {
    return `${sign}${symbol}${absNum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
};

// Format number with Indian comma separation
export const formatIndianNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Format date to DD/MM/YYYY
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Format date for input fields (YYYY-MM-DD)
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  return toLocalISODate(date);
};

// Local-timezone YYYY-MM-DD (toISOString shifts IST dates to the previous UTC day)
export const toLocalISODate = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// State code from explicit field, else GSTIN prefix
export const stateOf = (entity) => entity?.state_code || (entity?.gstin || '').slice(0, 2);

// Get financial year
export const getFinancialYear = (date = new Date()) => {
  const month = date.getMonth();
  const year = date.getFullYear();
  if (month >= 3) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  }
  return `${year - 1}-${year.toString().slice(-2)}`;
};

// Get status color classes
export const getStatusClasses = (status) => {
  const statusMap = {
    draft: 'bg-stone-100 text-stone-700',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    cancelled: 'bg-stone-100 text-stone-500',
    partially_paid: 'bg-amber-100 text-amber-700',
    recorded: 'bg-blue-100 text-blue-700',
  };
  return statusMap[status] || 'bg-stone-100 text-stone-700';
};

// Indian states with codes
export const INDIAN_STATES = [
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '28', name: 'Andhra Pradesh' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh (New)' },
  { code: '38', name: 'Ladakh' },
];

// GST rates
export const GST_RATES = [0, 5, 12, 18, 28];

// Expense categories
export const EXPENSE_CATEGORIES = [
  'Office Supplies',
  'Travel',
  'Utilities',
  'Rent',
  'Professional Services',
  'Marketing',
  'Software & Subscriptions',
  'Equipment',
  'Telecommunications',
  'Insurance',
  'Legal & Compliance',
  'Other',
];

// Business types
export const BUSINESS_TYPES = [
  { value: 'sole_prop', label: 'Sole Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'pvt_ltd', label: 'Private Limited' },
  { value: 'llp', label: 'LLP' },
  { value: 'opc', label: 'One Person Company' },
  { value: 'freelancer', label: 'Freelancer' },
];

// Validate GSTIN format
export const validateGSTIN = (gstin) => {
  if (!gstin) return true; // Optional
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstinRegex.test(gstin);
};

// Validate PAN format
export const validatePAN = (pan) => {
  if (!pan) return true; // Optional
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
};

// Calculate GST
export const calculateGST = (amount, gstRate, isIGST = false) => {
  const taxAmount = (amount * gstRate) / 100;
  if (isIGST) {
    return { cgst: 0, sgst: 0, igst: taxAmount, totalTax: taxAmount };
  }
  const halfTax = taxAmount / 2;
  return { cgst: halfTax, sgst: halfTax, igst: 0, totalTax: taxAmount };
};
